// Theme entry for the Brooklyn product page and cart page (replaces the preview-only entry).
// Exactly one [data-jbfc-root] per document. The section's JSON config chooses the mode:
//   nativeTransportEnabled:true  -> the explicit customer binding (real /cart/add.js and
//                                   /cart/change.js through the reviewed transport + journal)
//   anything else                -> display-only (no controller, Add stays disabled)
// The config can only choose between those two reviewed constructions; it supplies no fetch,
// controller, origin, pins or storage. Data assets are read from the theme's own asset URLs.
import {createIntegratedApp} from './jbfc-integrated-app.js?v=l2-037d5d03501f';
import {createCustomerBinding} from './jbfc-customer-binding.js?v=l2-037d5d03501f';
import {syncPage} from './jbfc-theme-page.js?v=l2-037d5d03501f';

export const DATA_ASSETS = Object.freeze({profiles: 'jbfc-admitted-profiles.json', catalog: 'jbfc-admitted-catalog.json'});
const installs = new WeakMap();

export function installThemeEntry({document: doc = globalThis.document, win = globalThis, fetchPublic = (url, options) => globalThis.fetch(url, options), createBinding = createCustomerBinding, createApp = createIntegratedApp} = {}) {
  if (installs.has(doc)) return installs.get(doc);
  let current = null, disposed = false;
  const message = (root, text) => {const s = root.querySelector('[data-jbfc-status]'); if (s) {s.textContent = text; s.setAttribute('role', 'alert');}};
  const roots = () => [...doc.querySelectorAll('[data-jbfc-root]')].filter(r => r.isConnected);
  const stale = record => record.retired || disposed || current !== record || !record.root.isConnected;
  const booted = () => doc.documentElement.classList.remove('jbfc-booting');
  async function boot() {
    if (disposed) return null;
    const found = roots();
    if (found.length !== 1) {if (current) retire(current.root); for (const root of found) message(root, 'Only one meal section can take orders on a page.'); booted(); return null;}
    const root = found[0];
    if (current?.root === root) return current.promise;
    if (current) {const stopped = retire(current.root); if (stopped.status === 'recovery_required') {message(root, 'A previous cart change needs inspection.'); return null;}}
    const node = root.querySelector('[data-jbfc-config]');
    if (!node) {booted(); return null;}
    const record = {root, abort: new AbortController(), retired: false, instance: null, unsubscribe: null, onDrawer: null, onPageShow: null, promise: null};
    current = record;
    record.promise = (async () => {
      try {
        const config = JSON.parse(node.textContent);
        const read = async (url, name) => {
          const u = new URL(url, doc.baseURI);
          if (u.protocol !== 'https:' || !u.pathname.endsWith('/' + name)) throw Error('Unexpected meal data source');
          // Shopify versions asset URLs by content (?v=), and the binding pins the data's SHA-256,
          // so the browser cache may serve these files across pages.
          const r = await fetchPublic(u.href, {credentials: 'omit', cache: 'default', signal: record.abort.signal});
          if (!r.ok) throw Error('Meal choices could not load. Please refresh the page.');
          return r.json();
        };
        const [profiles, catalog] = await Promise.all([read(config.profilesUrl, DATA_ASSETS.profiles), read(config.catalogUrl, DATA_ASSETS.catalog)]);
        if (stale(record)) return null;
        const productId = config.productId ?? null;
        if (config.nativeTransportEnabled === true) {
          const binding = createBinding({fetch: (url, options) => win.fetch(url, options), origin: win.location.origin, localeRoot: config.localeRoot, indexedDB: win.indexedDB, crypto: win.crypto});
          record.instance = await binding.mount(root, {profiles, catalog, productId, copy: config.copy});
        } else {
          const app = createApp({profiles, catalog, copy: config.copy});
          record.instance = await app.mount(root, {productId});
        }
        if (stale(record)) {record.instance?.destroy?.(); return null;}
        root.dataset.jbfcMounted = record.instance.mode ?? 'mounted';
        const status = root.querySelector('[data-jbfc-status][data-jbfc-loading]');
        if (status) {
          delete status.dataset.jbfcLoading;
          // An earlier add that a closed page left unfinished, and which did not reach the cart, is said once.
          const last = record.instance.controller?.state().lastResult;
          if (status.getAttribute('role') !== 'alert' && record.instance.controller) status.textContent = last?.status === 'not_added' ? last.message : config.copy?.ready ?? '';
        }
        const controller = record.instance.controller;
        if (controller) {
          const serverCount = Number(root.dataset.jbfcServerItemCount ?? 'NaN');
          const sync = state => {
            try {syncPage(doc, state, {win});} catch {/* page cosmetics never block the meal controls */}
            // The cart page is server drawn; an emptied cart shows Brooklyn's own empty state.
            if (doc.querySelector('[data-jbfc-cart-page]') && serverCount > 0 && state?.cart?.item_count === 0 && !state.busy && !state.held) win.location.reload();
          };
          record.unsubscribe = controller.subscribe(sync); sync(controller.state());
          // A page restored from the back/forward cache re-reads Shopify's cart before showing meals.
          record.onPageShow = event => {if (event.persisted && !controller.state().busy) controller.refresh().catch(() => {});};
          win.addEventListener('pageshow', record.onPageShow);
          // Brooklyn rebuilds its drawer from /cart.js; re-freeze meal lines after every rebuild.
          const $ = win.jQuery;
          if (typeof $ === 'function') {
            record.onDrawer = () => {try {syncPage(doc, controller.state(), {win});} catch {/* cosmetic */} if (!controller.state().busy) controller.refresh().catch(() => {});};
            $(doc.body).on('ajaxCart.afterCartLoad', record.onDrawer);
          }
        }
        return record.instance;
      } catch (error) {
        if (!record.retired && root.isConnected) message(root, error.message);
        try {syncPage(doc, null, {win});} catch {/* cosmetic */}
        return null;
      } finally {booted();}
    })();
    return record.promise;
  }
  function retire(root) {
    if (!current || current.root !== root) return {status: 'absent'};
    const r = current; r.retired = true; r.abort.abort();
    const result = r.instance?.destroy?.() ?? {status: 'disposed'};
    if (result.status !== 'recovery_required') {
      r.unsubscribe?.();
      if (r.onPageShow) win.removeEventListener('pageshow', r.onPageShow);
      if (r.onDrawer && typeof win.jQuery === 'function') win.jQuery(doc.body).off('ajaxCart.afterCartLoad', r.onDrawer);
      delete root.dataset.jbfcMounted; current = null;
    }
    return result;
  }
  const load = () => void boot(), unload = e => {if (current && (e.target === current.root || e.target?.contains?.(current.root))) retire(current.root);};
  doc.addEventListener('shopify:section:load', load); doc.addEventListener('shopify:section:unload', unload);
  const instance = Object.freeze({boot, retire, state: () => current?.instance?.controller?.state() ?? null, destroy() {if (current && retire(current.root).status === 'recovery_required') return {status: 'recovery_required'}; disposed = true; doc.removeEventListener('shopify:section:load', load); doc.removeEventListener('shopify:section:unload', unload); installs.delete(doc); return {status: 'disposed'};}});
  installs.set(doc, instance);
  return instance;
}

if (globalThis.document && !globalThis.__jbfcThemeEntryDisabled) {
  // Read-only observation hook (tests, support): the controller itself is never exposed.
  const start = () => {const entry = installThemeEntry(); globalThis.jbfcThemeEntry = Object.freeze({state: () => entry.state()}); void entry.boot();};
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, {once: true});
  else queueMicrotask(start);
}
